import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../config/environment';

const API_BASE_URL = getApiUrl();

interface CallTemplate {
  id: string;
  name: string;
  description?: string;
  script_content: string;
  assistant_config_id?: string;
  status: 'active' | 'inactive';
}

interface VapiAssistant {
  id: string;
  name: string;
  description?: string;
  assistant_id: string;
  phone_number_id: string;
  status: 'active' | 'inactive';
  is_default: boolean;
}

const CallTemplateManager: React.FC = () => {
  const [templates, setTemplates] = useState<CallTemplate[]>([]);
  const [assistants, setAssistants] = useState<VapiAssistant[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<CallTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    script_content: '',
    assistant_config_id: ''
  });

  useEffect(() => {
    loadTemplates();
    loadAssistants();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/vapi/call-templates`);
      setTemplates(response.data.data || []);
    } catch (error) {
      console.error('Error loading call templates:', error);
    }
  };

  const loadAssistants = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/vapi/assistants`);
      setAssistants(response.data.data || []);
    } catch (error) {
      console.error('Error loading VAPI assistants:', error);
    }
  };

  const handleAddTemplate = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      description: '',
      script_content: '',
      assistant_config_id: ''
    });
    setOpenDialog(true);
  };

  const handleEditTemplate = (template: CallTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      script_content: template.script_content,
      assistant_config_id: template.assistant_config_id || ''
    });
    setOpenDialog(true);
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (window.confirm('Tem certeza que deseja excluir este template?')) {
      try {
        await axios.delete(`${API_BASE_URL}/vapi/call-templates/${templateId}`);
        await loadTemplates();
      } catch (error) {
        console.error('Error deleting template:', error);
      }
    }
  };

  const handleSaveTemplate = async () => {
    try {
      setLoading(true);

      if (editingTemplate) {
        await axios.put(`${API_BASE_URL}/vapi/call-templates/${editingTemplate.id}`, formData);
      } else {
        await axios.post(`${API_BASE_URL}/vapi/call-templates`, formData);
      }

      setOpenDialog(false);
      await loadTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAssistantName = (assistantId: string) => {
    const assistant = assistants.find(a => a.id === assistantId);
    return assistant ? assistant.name : 'Assistente não encontrado';
  };

  const getDefaultAssistant = () => {
    return assistants.find(a => a.is_default);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center' }}>
          <PhoneIcon sx={{ mr: 2 }} />
          Templates de Ligação VAPI
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddTemplate}
        >
          Novo Template
        </Button>
      </Box>

      {templates.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary" align="center">
              Nenhum template de ligação encontrado. Crie seu primeiro template para começar.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <List>
          {templates.map((template) => (
            <Card key={template.id} sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      {template.name}
                    </Typography>
                    {template.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {template.description}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Script:</strong> {template.script_content.substring(0, 100)}...
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <PersonIcon sx={{ fontSize: 16 }} />
                      <Typography variant="body2" color="text.secondary">
                        <strong>Assistente:</strong> {
                          template.assistant_config_id 
                            ? getAssistantName(template.assistant_config_id)
                            : `${getDefaultAssistant()?.name || 'Assistente padrão'} (Padrão)`
                        }
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 1 }}>
                      <Chip 
                                        label={template.status === 'active' ? 'Ativo' : 'Inativo'}
                color={template.status === 'active' ? 'success' : 'default'} 
                        size="small" 
                      />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditTemplate(template)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteTemplate(template.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </List>
      )}

      {/* Template Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTemplate ? 'Editar Template de Ligação' : 'Novo Template de Ligação'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Nome do Template"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />

            <TextField
              label="Descrição"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />

            <FormControl fullWidth>
              <InputLabel>Assistente VAPI</InputLabel>
              <Select
                value={formData.assistant_config_id}
                onChange={(e) => setFormData({ ...formData, assistant_config_id: e.target.value })}
                label="Assistente VAPI"
              >
                <MenuItem value="">
                  <em>Usar assistente padrão</em>
                </MenuItem>
                {assistants.map((assistant) => (
                  <MenuItem key={assistant.id} value={assistant.id}>
                    <Box>
                      <Typography variant="body1">
                        {assistant.name}
                        {assistant.is_default && (
                          <Chip label="Padrão" size="small" sx={{ ml: 1 }} />
                        )}
                      </Typography>
                      {assistant.description && (
                        <Typography variant="caption" color="text.secondary">
                          {assistant.description}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Script da Ligação"
              value={formData.script_content}
              onChange={(e) => setFormData({ ...formData, script_content: e.target.value })}
              fullWidth
              multiline
              rows={6}
              required
              helperText="Digite o script que o assistente VAPI deve seguir durante a ligação. Use [NOME_CONTATO] para personalizar com o nome do contato."
            />

            <Alert severity="info">
              <Typography variant="body2">
                <strong>Dicas para o script:</strong>
              </Typography>
              <Typography variant="body2">
                • Use [NOME_CONTATO] para inserir o nome do contato automaticamente
              </Typography>
              <Typography variant="body2">
                • Seja natural e conversacional
              </Typography>
              <Typography variant="body2">
                • Inclua perguntas para qualificar o lead
              </Typography>
              <Typography variant="body2">
                • Defina claramente o objetivo da ligação
              </Typography>
            </Alert>

            {formData.assistant_config_id && (
              <Alert severity="success">
                <Typography variant="body2">
                  <strong>Assistente selecionado:</strong> {getAssistantName(formData.assistant_config_id)}
                </Typography>
              </Alert>
            )}

            {!formData.assistant_config_id && getDefaultAssistant() && (
              <Alert severity="warning">
                <Typography variant="body2">
                  <strong>Usando assistente padrão:</strong> {getDefaultAssistant()?.name}
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button 
            onClick={handleSaveTemplate} 
            variant="contained"
            disabled={loading || !formData.name || !formData.script_content}
          >
            {loading ? 'Salvando...' : (editingTemplate ? 'Atualizar' : 'Criar')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CallTemplateManager; 